import React from 'react';
import { compose } from 'recompose';
import styled from 'styled-components';
import deepUpdateOnKeys from '@peloton/hocs/deepUpdateOnKeys';
import { ellipsis, defaultTransition, media } from '@peloton/styles';
import { white, gray1, slate3, slate1 } from '@engage/colors';
import type { SortValue } from '@engage/filters';
import { useFilterContext } from '@engage/filters';
import { spaces } from '@engage/styles';
import { label12, label14Caps, label2Large } from '@engage/typography';
import { useClasses } from '@members/copy';
import { Classes } from '@members/copy/models.generated/Classes';
import { Chevron, Orientation } from '@members/icons';
import { Collapsible } from './Collapsible';
import type { Option } from './hooks';
import { useMultiSelectLabel, useStickyFilter } from './hooks';

const SELECTED_CLASSNAME = 'selected-accordion';
const HEADER_HEIGHT = 67;

const Wrapper = styled.div`
  background: ${white};
  border-radius: 5px;
  width: 100%;
`;

const AccordionButton = styled.button<{
  isStickyApplied: boolean;
  isOpen: boolean;
}>`
  display: flex;
  width: ${p => (p.isStickyApplied ? 'calc(100vw + 20px)' : '100%')};
  flex: 1;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  background-color: ${white};
  top: ${HEADER_HEIGHT}px;
  color: ${slate1};
  transition: all 0.3s ease-in;
  padding: ${p =>
    p.isStickyApplied ? `0 ${spaces.xxxLarge}px` : `0 ${spaces.medium}px`};
  height: 56px;
  z-index: ${p => (p.isOpen ? 1 : 0)};
  ${p => (p.isStickyApplied ? 'margin-left: -20px' : '')};
  box-shadow: ${p => (p.isStickyApplied ? '0px 2px 8px 8px rgba(0, 0, 0, 0.08)' : '')};
  border-radius: 0px;

  ${media.tabletXLarge<{ isStickyApplied: boolean }>`
    width: ${p => (p.isStickyApplied ? '440px' : '100%')};
  `};
`;

const List = styled.ul<{ columns: number }>`
  display: grid;
  grid-auto-rows: 1fr;
  grid-template-columns: ${({ columns }) => `repeat(${columns}, 1fr)`};
  margin-top: -10px;
  padding: 5px 0 10px 10px;
`;

const GroupTitle = styled.p`
  padding: 5px 0 10px 10px;
  ${label2Large}
`;

const ListTitle = styled.span`
  ${label14Caps}
  padding: ${spaces.xxxSmall}px ${spaces.xSmall}px;
`;

const ListTitleWithPadding = styled(ListTitle)`
  padding-left: ${spaces.small}px;
  padding-right: 10px;
`;

const ListItem = styled.li`
  background: transparent;
  padding-right: 10px;
  margin: 0;
  margin-top: 10px;
`;

const Button = styled.button`
  background: ${gray1};
  border-radius: 5px;
  color: ${slate1};
  height: 100%;
  width: 100%;
  ${defaultTransition('filter')}

  &:hover {
    filter: brightness(95%);
  }

  &.${SELECTED_CLASSNAME} {
    background: ${slate1};
    color: ${white};
  }
`;

const OptionItemView: React.FC<React.PropsWithChildren<OptionItemProps>> = ({
  onToggle,
  option,
  setIsOpen,
}) => {
  React.useEffect(() => {
    const handleEnter = (event: KeyboardEvent) => {
      if (event.code === 'Enter') {
        setTimeout(() => setIsOpen(false), 300);
      }
    };
    document.addEventListener('keydown', handleEnter);
    return () => {
      document.removeEventListener('keydown', handleEnter);
    };
  }, [onToggle]);

  return (
    <ListItem>
      <Button
        className={option.selected ? SELECTED_CLASSNAME : ''}
        onClick={() => onToggle(option.value)}
        aria-pressed={option.selected}
      >
        {option.content}
      </Button>
    </ListItem>
  );
};

const OptionItem = compose<{}, OptionItemProps>(
  deepUpdateOnKeys<OptionItemProps>(['option']),
)(OptionItemView);

const getSelectedOption = (option: Option) => !!option.selected;

const selectedOptionName = (options: Option[]) => (options ?? []).find(getSelectedOption);

const SummaryTextViewMulti: React.FC<
  React.PropsWithChildren<{
    option: (Option | undefined)[];
    additionalLabel: number;
    summaryLabel: string;
  }>
> = ({ option, additionalLabel, summaryLabel, ...rest }) => {
  return (
    <>
      {additionalLabel ? (
        <SecondaryTextBox {...rest}>
          {option?.length.toString()} {summaryLabel}
        </SecondaryTextBox>
      ) : (
        option?.slice(0, option.length).map(o => (
          <SecondaryTextBox key={o?.label} {...rest}>
            {o && o.label ? o.label : ''}
          </SecondaryTextBox>
        ))
      )}
    </>
  );
};

const SecondaryTextViewMulti: React.FC<
  React.PropsWithChildren<{
    option?: (Option | undefined)[];
    additionalLabel: number;
  }>
> = ({ option, additionalLabel, ...rest }) => {
  return (
    <>
      {option
        ?.slice(0, additionalLabel > 0 ? option.length - additionalLabel : option.length)
        .map(o => {
          return (
            <SecondaryTextBox key={o?.label} {...rest}>
              {o && o.label ? o.label : ''}
            </SecondaryTextBox>
          );
        })}
      {additionalLabel > 0 ? (
        <SecondaryTextBox {...rest}>{'+' + additionalLabel.toString()}</SecondaryTextBox>
      ) : null}
    </>
  );
};

const SecondaryTextBox = styled.span`
  background-color: ${gray1};
  border-radius: 2px;
  padding: 4px 6px;
  margin-left: 6px;
`;

const SummaryTextView = styled(SummaryTextViewMulti)`
  ${ellipsis}
  ${label12}
  color: ${slate3};
`;

const SecondaryTextMulti = styled(SecondaryTextViewMulti)`
  ${ellipsis}
  ${label12}
  color: ${slate3};
`;

const AnimatedChevron = styled(Chevron)`
  ${defaultTransition('transform')}
  fill: ${slate1};
  margin: ${spaces.xxxxSmall}px 0 0 10px;
  min-width: 14px;
`;

const SelectedOptionAndDropdownControl = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
`;

const IconContainer = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  flex-shrink: 0;
`;

const OuterContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
`;

const InnerContainer = styled.div`
  width: fit-content;
`;

const Navtop = styled.div`
  height: 1px;
`;
export const SelectableAccordion: React.FC<React.PropsWithChildren<WidgetProps>> = ({
  title,
  options,
  groupedOptions = {},
  onToggle,
  columns,
  icon,
  name,
}) => {
  const [isOpen, setIsOpen] = useFilterContext(title);
  const ref = React.useRef<HTMLDivElement>(null);
  const parentRef = React.useRef<HTMLDivElement>(null);
  const summaryLabel = useClasses(Classes.SummaryOfItems);
  const { additionalLabel, orderedOptionNames } = useMultiSelectLabel(
    ref,
    parentRef,
    options,
    name,
  );
  /**
   * ids cannot have certain special characters like :
   * we're being safe since the title comes from API and can crash
   * the whole page if the selector is incorrect
   */
  const cleanedTitle = title.replace(/[^a-zA-Z ]/g, '').replace(' ', '');

  const { isStickyApplied } = useStickyFilter(cleanedTitle);

  const generateOptions = (listOptions: any) => (
    <List id={`collapsible-${cleanedTitle}`} columns={columns}>
      {listOptions.map((o: Option, i: number) => (
        <OptionItem
          key={`OptionItem-${i}`}
          option={o}
          onToggle={onToggle}
          columns={columns}
          setIsOpen={setIsOpen}
        />
      ))}
    </List>
  );

  const generateOptionsView = () => {
    if (Object.keys(groupedOptions).length) {
      return (
        <ul>
          {Object.keys(groupedOptions).map((option: any) => {
            return (
              <li key={`collapsible-${option}`}>
                <GroupTitle>{option}</GroupTitle>
                {generateOptions(groupedOptions[option])}
              </li>
            );
          })}
        </ul>
      );
    }
    return generateOptions(options);
  };

  return (
    <Wrapper>
      <Navtop id={`nav-container-top-${cleanedTitle}`} />
      <AccordionButton
        data-test-id="accordionCell"
        aria-controls={`collapsible-${cleanedTitle}`}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        id={`accordion-button-${cleanedTitle}`}
        isStickyApplied={isStickyApplied && isOpen}
        isOpen={isOpen}
      >
        {icon ? (
          <IconContainer>
            {icon} <ListTitleWithPadding>{title}</ListTitleWithPadding>
          </IconContainer>
        ) : (
          <ListTitle>{title}</ListTitle>
        )}
        <SelectedOptionAndDropdownControl data-test-id="selectedOption">
          <OuterContainer ref={parentRef}>
            <InnerContainer ref={ref}>
              {name === 'muscle_group' ? (
                <SummaryTextView
                  option={orderedOptionNames}
                  additionalLabel={additionalLabel}
                  summaryLabel={summaryLabel}
                />
              ) : (
                <SecondaryTextMulti
                  option={
                    name === 'Sort' ? [selectedOptionName(options)] : orderedOptionNames
                  }
                  additionalLabel={additionalLabel}
                />
              )}
            </InnerContainer>
          </OuterContainer>
          <AnimatedChevron
            orientation={isOpen ? Orientation.Up : Orientation.Down}
            width={14}
            data-test-id="accordionArrow"
          />
        </SelectedOptionAndDropdownControl>
      </AccordionButton>
      <Collapsible id={`collapsible-${cleanedTitle}`} isOpen={isOpen}>
        {generateOptionsView()}
      </Collapsible>
    </Wrapper>
  );
};

type OptionItemProps = {
  onToggle: ToggleHandler;
  columns: number;
  option: Option;
  setIsOpen: (isOpen: boolean) => void;
};

type ToggleHandler = (value: string | SortValue) => void;

type WidgetProps = {
  title: string;
  options: Option[];
  groupedOptions?: { [key: string]: Option[] };
  onToggle: ToggleHandler;
  columns: number;
  icon?: JSX.Element;
  name?: string;
};
